import { SEOModule } from '../types';

export const MODULE_1_LECTURES_31_33: SEOModule = {
  id: 'module-1-lectures-31-33',
  name: 'Zynex Ninja Enterprise Audit Engine',
  version: 'zynex-v1.0',
  description: 'Beginner-friendly, proprietary SEO audit engine implementing Zynex Ninja technical standards.',
  lectureSources: ['lec31-SEO Audit Part 1.txt', 'lec32-SEO Audit Part 2.txt', 'lec33-SEO Audit Part 3.txt'],
  weightingRules: {
    onPageWeight: 0.66,
    offPageWeight: 0.34,
  },
  sections: [
    {
      id: 'sec-project-setup',
      title: 'Project Setup & Domain Information',
      icon: 'Building',
      description: 'Initialize the audit by capturing client and domain details.',
      steps: [
        {
          id: 'step-setup-client',
          sectionId: 'sec-project-setup',
          stepNumber: 1,
          title: 'Setup Client & Website Details',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Manual Entry',
          what: 'Record the client name and target website URL for the SEO audit.',
          why: 'Every professional SEO audit begins by establishing the client identity and domain destination.',
          how: [
            'Obtain the target website domain from your client.',
            'Enter the client or company name.',
            'Enter the full website URL (e.g., https://example.com).'
          ],
          fields: [
            {
              id: 'clientName',
              label: 'Client Name / Business Name',
              type: 'string',
              required: true,
              placeholder: 'e.g. ABC Construction Co.'
            },
            {
              id: 'websiteUrl',
              label: 'Target Website URL',
              type: 'url',
              required: true,
              placeholder: 'https://example.com'
            }
          ]
        }
      ]
    },
    {
      id: 'sec-screaming-frog-titles',
      title: 'Screaming Frog — SEO Page Titles',
      icon: 'FileText',
      description: 'Audit page titles for missing, duplicate, long, and short issues using Screaming Frog.',
      steps: [
        {
          id: 'step-titles-total',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 2,
          title: 'Find Total Crawled Pages',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          toolUrl: 'https://www.screamingfrog.co.uk/seo-spider/',
          what: 'Crawl the client website to find the total number of pages fetched by Screaming Frog.',
          why: 'This total serves as the denominator for calculating title, description, and heading issue percentages.',
          how: [
            'Open Screaming Frog SEO Spider.',
            'Enter the client website URL in the top address bar and click "Start".',
            'Wait for the crawl progress bar to reach completion (or 500 pages limit on free version).',
            'Select the "Page Titles" tab at the top.',
            'Look at the bottom status bar for the "Total" number of page titles/pages fetched.'
          ],
          fields: [
            {
              id: 'totalPages',
              label: 'Total Pages Crawled / Fetched',
              type: 'integer',
              required: true,
              min: 1,
              placeholder: 'e.g. 68'
            }
          ]
        },
        {
          id: 'step-titles-missing',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 3,
          title: 'Find Missing Page Titles',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify pages that completely lack an SEO page title.',
          why: 'Missing page titles mean search engines cannot understand page topics and Google cannot display proper SERP titles.',
          how: [
            'In Screaming Frog under "Page Titles" tab, click the "Filter" dropdown.',
            'Select "Missing".',
            'Look at the total count of missing page titles.',
            'Copy the URLs of affected pages from Screaming Frog and paste them below so they appear in the final report.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'missingTitles',
              label: 'Number of Pages Missing Page Titles',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 0'
            },
            {
              id: 'missingTitlesUrls',
              label: 'Affected Page URLs (Paste URLs of pages missing titles)',
              type: 'text',
              required: false,
              placeholder: 'Paste affected page URLs (one per line), e.g.:\nhttps://example.com/services\nhttps://example.com/contact-us',
              helpText: 'Enter specific page URLs facing this issue so they appear in the final report for client remediation.'
            }
          ],
          calculations: [
            {
              id: 'missingTitlesPct',
              label: 'Missing Page Titles Percentage',
              formula: 'percentage',
              affectedFieldId: 'missingTitles',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Add clear, keyword-focused SEO page titles to all pages currently missing titles.'
        },
        {
          id: 'step-titles-long',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 4,
          title: 'Find Long Page Titles (>60 Characters)',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Find SEO titles exceeding Google recommended length of approximately 60 characters.',
          why: 'Titles exceeding ~60 characters get truncated by Google in SERPs (Google Truncation). Keywords placed near the end get cut off, harming CTR and overall site impression.',
          how: [
            'In Screaming Frog under "Page Titles" tab, select Filter: "Over 60 Characters".',
            'Record the total number of long page titles shown.',
            'Copy sample affected URLs from Screaming Frog to include in the client report.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'longTitles',
              label: 'Number of Pages with Titles Over 60 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 20'
            },
            {
              id: 'longTitlesUrls',
              label: 'Affected Page URLs (Paste sample long title URLs)',
              type: 'text',
              required: false,
              placeholder: 'Paste affected URLs (one per line), e.g.:\nhttps://example.com/commercial-construction-services-in-chicago-illinois',
              helpText: 'List problem URLs so you and the client can review which page titles require shortening.'
            }
          ],
          calculations: [
            {
              id: 'longTitlesPct',
              label: 'Long Page Titles Percentage',
              formula: 'percentage',
              affectedFieldId: 'longTitles',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Shorten page titles exceeding 60 characters so primary keywords remain visible before Google SERP truncation.'
        },
        {
          id: 'step-titles-short',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 5,
          title: 'Find Short Page Titles (<30 Characters)',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify titles that are under Google lower reference length of approximately 30 characters.',
          why: 'Page titles under 30 characters fail to utilize valuable SERP space to communicate page relevance to users and search engines.',
          how: [
            'In Screaming Frog under "Page Titles" tab, select Filter: "Below 30 Characters" (or inspect titles < 30 chars).',
            'Record the total number of short page titles and copy affected page URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'shortTitles',
              label: 'Number of Pages with Titles Below 30 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 3'
            },
            {
              id: 'shortTitlesUrls',
              label: 'Affected Page URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste affected URLs (one per line)',
              helpText: 'List page URLs with short titles to expand in the final report.'
            }
          ],
          calculations: [
            {
              id: 'shortTitlesPct',
              label: 'Short Page Titles Percentage',
              formula: 'percentage',
              affectedFieldId: 'shortTitles',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Expand short titles (<30 characters) to include targeted keywords and brand descriptors.'
        },
        {
          id: 'step-titles-duplicate',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 6,
          title: 'Find Duplicate Page Titles',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Find separate pages on the website that share identical SEO titles.',
          why: 'Duplicate titles cause search engine confusion about which page to rank for target terms.',
          how: [
            'In Screaming Frog under "Page Titles" tab, select Filter: "Duplicate".',
            'Record the number of pages sharing duplicate titles and paste affected page URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'duplicateTitles',
              label: 'Number of Pages with Duplicate Titles',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 0'
            },
            {
              id: 'duplicateTitlesUrls',
              label: 'Affected Duplicate Title URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste URLs sharing duplicate titles (one per line)',
              helpText: 'Record URLs sharing identical page titles.'
            }
          ],
          calculations: [
            {
              id: 'duplicateTitlesPct',
              label: 'Duplicate Page Titles Percentage',
              formula: 'percentage',
              affectedFieldId: 'duplicateTitles',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Rewrite duplicate page titles so every single page possesses a unique, descriptive SEO title.'
        },
        {
          id: 'step-titles-same-h1',
          sectionId: 'sec-screaming-frog-titles',
          stepNumber: 7,
          title: 'Find Page Titles Same as H1',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify pages where the SEO page title (<title>) is identical to the main heading (<h1>).',
          why: 'While not an error, having identical Title and H1 limits SEO optimization. Page titles should include target location modifiers, brand names, or SERP CTR hooks within 60 characters, while H1 headings focus on user retention.',
          how: [
            'In Screaming Frog under "Page Titles" tab, select Filter: "Same as H1".',
            'Record the total count of pages where Title is identical to H1.',
            'Copy sample affected URLs from Screaming Frog and paste them below for client review.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'sameAsH1Titles',
              label: 'Number of Pages with Title Same as H1',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 15'
            },
            {
              id: 'sameAsH1TitlesUrls',
              label: 'Affected Page URLs (Title Same as H1)',
              type: 'text',
              required: false,
              placeholder: 'Paste affected URLs (one per line), e.g.:\nhttps://example.com/about-us',
              helpText: 'List page URLs where the page title and H1 heading are 100% identical.'
            }
          ],
          calculations: [
            {
              id: 'sameAsH1TitlesPct',
              label: 'Title Same as H1 Percentage',
              formula: 'percentage',
              affectedFieldId: 'sameAsH1Titles',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Optimize page titles to differ slightly from H1 headings by adding primary location keywords, brand descriptors, or compelling CTR hooks.'
        }
      ]
    },
    {
      id: 'sec-screaming-frog-meta',
      title: 'Screaming Frog — Meta Descriptions',
      icon: 'AlignLeft',
      description: 'Audit meta descriptions to maximize SERP click-through rate (CTR).',
      steps: [
        {
          id: 'step-meta-missing',
          sectionId: 'sec-screaming-frog-meta',
          stepNumber: 8,
          title: 'Find Missing Meta Descriptions',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify pages missing a meta description.',
          why: 'Meta descriptions display in SERPs under the title. Their primary role is to convince users to click, directly increasing Click-Through Rate (CTR).',
          how: [
            'In Screaming Frog, select the "Meta Description" tab.',
            'Select Filter: "Missing".',
            'Record the count of pages missing meta descriptions and copy affected URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'missingMeta',
              label: 'Number of Pages Missing Meta Descriptions',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 3'
            },
            {
              id: 'missingMetaUrls',
              label: 'Affected Page URLs (Missing Meta Description)',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs missing meta descriptions (one per line)',
              helpText: 'Record URLs missing meta descriptions for report remediation.'
            }
          ],
          calculations: [
            {
              id: 'missingMetaPct',
              label: 'Missing Meta Description Percentage',
              formula: 'percentage',
              affectedFieldId: 'missingMeta',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Craft engaging meta descriptions for all pages currently missing them.'
        },
        {
          id: 'step-meta-long',
          sectionId: 'sec-screaming-frog-meta',
          stepNumber: 9,
          title: 'Find Long Meta Descriptions (>161 Characters)',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify meta descriptions exceeding 161 characters.',
          why: 'Google truncates meta descriptions exceeding 161 characters, cutting off call-to-actions and valuable information.',
          how: [
            'In Screaming Frog under "Meta Description" tab, select Filter: "Over 155 Characters" or inspect items > 161 chars.',
            'Record the number of long meta descriptions and paste affected URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'longMeta',
              label: 'Number of Pages with Meta Descriptions Over 161 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 68'
            },
            {
              id: 'longMetaUrls',
              label: 'Affected Page URLs (Long Meta Description)',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs with long meta descriptions (one per line)',
              helpText: 'List URLs with long descriptions to trim.'
            }
          ],
          calculations: [
            {
              id: 'longMetaPct',
              label: 'Long Meta Description Percentage',
              formula: 'percentage',
              affectedFieldId: 'longMeta',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Trim long meta descriptions to under 161 characters to prevent SERP truncation.'
        },
        {
          id: 'step-meta-short',
          sectionId: 'sec-screaming-frog-meta',
          stepNumber: 10,
          title: 'Find Short Meta Descriptions (<70 Characters)',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify meta descriptions under 70 characters.',
          why: 'Meta descriptions under 70 characters fail to utilize available SERP snippet space to communicate page value and drive user clicks.',
          how: [
            'In Screaming Frog under "Meta Description" tab, select Filter: "Below 70 Characters".',
            'Record the count of short meta descriptions and copy affected page URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'shortMeta',
              label: 'Number of Pages with Meta Descriptions Below 70 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 5'
            },
            {
              id: 'shortMetaUrls',
              label: 'Affected Page URLs (Short Meta Description)',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs with short meta descriptions (one per line)',
              helpText: 'List page URLs featuring short meta descriptions under 70 characters.'
            }
          ],
          calculations: [
            {
              id: 'shortMetaPct',
              label: 'Short Meta Description Percentage',
              formula: 'percentage',
              affectedFieldId: 'shortMeta',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Expand short meta descriptions (<70 characters) to 140–160 characters to include compelling value propositions and clear calls to action.'
        },
        {
          id: 'step-meta-duplicate',
          sectionId: 'sec-screaming-frog-meta',
          stepNumber: 11,
          title: 'Find Duplicate Meta Descriptions',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Identify pages sharing identical meta descriptions.',
          why: 'Duplicate descriptions reduce CTR potential across different search queries.',
          how: [
            'In Screaming Frog under "Meta Description" tab, select Filter: "Duplicate".',
            'Record the count of duplicate meta descriptions and paste affected URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'duplicateMeta',
              label: 'Number of Pages with Duplicate Meta Descriptions',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 2'
            },
            {
              id: 'duplicateMetaUrls',
              label: 'Affected Duplicate Meta Description URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste URLs sharing duplicate meta descriptions (one per line)',
              helpText: 'List URLs sharing duplicate descriptions.'
            }
          ],
          calculations: [
            {
              id: 'duplicateMetaPct',
              label: 'Duplicate Meta Description Percentage',
              formula: 'percentage',
              affectedFieldId: 'duplicateMeta',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Write unique meta descriptions customized to each individual page.'
        },
        {
          id: 'step-meta-keywords',
          sectionId: 'sec-screaming-frog-meta',
          stepNumber: 12,
          title: 'Check Meta Keywords (Ignored)',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Instructor Teaching',
          what: 'Verify meta keywords handling.',
          why: 'Google officially ignores meta keywords tag. The instructor explicitly teaches that we ignore meta keywords during SEO audits.',
          how: [
            'Acknowledge that meta keywords are ignored by Google.',
            'Confirm that no effort will be wasted analyzing or adding meta keywords.'
          ],
          fields: [
            {
              id: 'metaKeywordsAcknowledged',
              label: 'Confirm Meta Keywords Ignored',
              type: 'boolean',
              required: true,
              defaultValue: true
            }
          ]
        }
      ]
    },
    {
      id: 'sec-screaming-frog-headings',
      title: 'Screaming Frog — Headings (H1 & H2)',
      icon: 'Heading',
      description: 'Audit H1 and H2 heading structure and user retention elements.',
      steps: [
        {
          id: 'step-h1-audit',
          sectionId: 'sec-screaming-frog-headings',
          stepNumber: 13,
          title: 'Audit H1 Headings',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Audit missing, duplicate, long (>70 chars), multiple (>1 H1), and non-sequential H1 headings.',
          why: 'While SEO title gets the click from SERP, the H1 main heading retains the user on page. In WordPress, default post/page title serves as H1.',
          how: [
            'In Screaming Frog, click the "H1" tab.',
            'Check Filter: "Missing", Filter: "Duplicate", Filter: "Over 70 Characters", Filter: "Multiple", and Filter: "Non-Sequential".',
            'Record the respective page counts and copy affected URLs for each H1 issue.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'missingH1',
              label: 'Pages Missing H1',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 0'
            },
            {
              id: 'missingH1Urls',
              label: 'Affected URLs Missing H1 Heading',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs missing H1 heading (one per line)',
              helpText: 'Record specific page URLs missing H1 heading.'
            },
            {
              id: 'duplicateH1',
              label: 'Pages with Duplicate H1',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 64'
            },
            {
              id: 'duplicateH1Urls',
              label: 'Affected URLs with Duplicate H1',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs sharing duplicate H1 headings (one per line)',
              helpText: 'Record page URLs sharing identical H1 headings.'
            },
            {
              id: 'longH1',
              label: 'Pages with H1 Over 70 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 12'
            },
            {
              id: 'longH1Urls',
              label: 'Affected URLs with H1 Over 70 Characters',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs with long H1 headings >70 chars (one per line)',
              helpText: 'Record page URLs with overly long H1 headings.'
            },
            {
              id: 'multipleH1',
              label: 'Pages with Multiple H1s',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 2'
            },
            {
              id: 'multipleH1Urls',
              label: 'Affected URLs with Multiple H1 Headings',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs featuring multiple H1 headings (one per line)',
              helpText: 'Record page URLs containing more than one H1 heading.'
            }
          ],
          calculations: [
            {
              id: 'duplicateH1Pct',
              label: 'Duplicate H1 Percentage',
              formula: 'percentage',
              affectedFieldId: 'duplicateH1',
              totalFieldId: 'totalPages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Ensure every page has exactly one primary H1 heading containing target keywords to retain users.'
        },
        {
          id: 'step-h2-audit',
          sectionId: 'sec-screaming-frog-headings',
          stepNumber: 14,
          title: 'Audit H2 Headings',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Audit missing, duplicate, and multiple H2 headings.',
          why: 'H2 subheadings emphasize key promises (e.g. "Affordable Customized Plans", "Get a free quote") and naturally incorporate target keywords.',
          how: [
            'In Screaming Frog, click the "H2" tab.',
            'Check total H2 usage, missing H2, and duplicate H2 pages.',
            'Note if generic H2s (like footer/navigation text) are duplicated across 100+ pages and copy sample URLs.'
          ],
          dependencies: ['step-titles-total'],
          fields: [
            {
              id: 'missingH2',
              label: 'Pages Missing H2',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 2'
            },
            {
              id: 'missingH2Urls',
              label: 'Affected URLs Missing H2 Headings',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs missing H2 subheadings (one per line)',
              helpText: 'Record URLs lacking H2 subheadings.'
            },
            {
              id: 'duplicateH2',
              label: 'Pages with Duplicate H2s',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 110'
            },
            {
              id: 'duplicateH2Urls',
              label: 'Affected URLs with Duplicate H2s',
              type: 'text',
              required: false,
              placeholder: 'Paste page URLs with duplicate H2 headings (one per line)',
              helpText: 'Record sample URLs featuring duplicate H2 headings.'
            },
            {
              id: 'multipleH2',
              label: 'Pages using Multiple H2s',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalPages',
              placeholder: 'e.g. 108'
            },
            {
              id: 'multipleH2Urls',
              label: 'Affected URLs using Multiple H2s',
              type: 'text',
              required: false,
              placeholder: 'Paste sample URLs using multiple H2 headings (one per line)',
              helpText: 'Record sample URLs with multiple H2 headings.'
            }
          ],
          recommendationTemplate: 'Use descriptive H2 subheadings to break up content and highlight key offers with target keywords.'
        }
      ]
    },
    {
      id: 'sec-content-keyword-focus',
      title: 'Content & Keyword Focus',
      icon: 'BookOpen',
      description: 'Audit keyword alignment, readability, and content growth velocity.',
      steps: [
        {
          id: 'step-keyword-focus',
          sectionId: 'sec-content-keyword-focus',
          stepNumber: 15,
          title: 'Identify Website Keyword Focus',
          lecture: 31,
          coverage: 'explicit',
          tool: 'Client Website Review',
          what: 'Check whether the website clearly targets a consistent set of keywords across SEO titles, descriptions, headings, body text, and alt text.',
          why: 'Without keyword focus, search engines cannot determine what search queries the website should rank for.',
          how: [
            'Open the client website homepage and main service/product pages.',
            'Review SEO title, meta description, H1, H2s, main content, and image alt text.',
            'Look for terms that repeatedly describe what the business offers.',
            'Select the observed keyword pattern checklist option below and record observed terms.'
          ],
          fields: [
            {
              id: 'observedTerm1',
              label: 'Primary Observed Keyword / Term',
              type: 'string',
              required: true,
              placeholder: 'e.g. Construction Company'
            },
            {
              id: 'observedTerm2',
              label: 'Secondary Observed Term',
              type: 'string',
              required: false,
              placeholder: 'e.g. Commercial Builder'
            },
            {
              id: 'observedKeywordPattern',
              label: 'Keyword Focus Assessment Checklist *',
              type: 'select',
              required: true,
              options: [
                { label: 'Clear Target — Key terms consistently present in Titles, Descriptions, H1, H2 & Copy', value: 'clear_target' },
                { label: 'Partial Target — Terms present in H1/Title, but missing in Meta Descriptions or Image Alt Text', value: 'partial_target' },
                { label: 'Unclear / Lacks Target — Generic terms used; search engines cannot identify primary target queries', value: 'unclear_target' }
              ]
            },
            {
              id: 'additionalComments',
              label: 'Additional Auditor Observations / Comments (Optional)',
              type: 'text',
              required: false,
              placeholder: 'Enter any additional notes regarding website keyword targeting...'
            }
          ],
          recommendationTemplate: 'Establish a clear keyword target across SEO titles, meta descriptions, headings, and page copy.'
        },
        {
          id: 'step-readability',
          sectionId: 'sec-content-keyword-focus',
          stepNumber: 16,
          title: 'Analyze Sentence Structure & Readability',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog / Content Tab',
          what: 'Record sentence count, average words per sentence, and Flesch Reading Ease score.',
          why: 'Google prioritizes user experience. Most top-ranking pages score between 60–63 on Flesch Reading Ease (starting from 30 up to ~60s).',
          how: [
            'In Screaming Frog, select the "Content" tab.',
            'Review readability metrics: Word Count, Sentence Count, Average Words Per Sentence, Flesch Reading Ease score.'
          ],
          fields: [
            {
              id: 'totalWordCount',
              label: 'Total Word Count (Sample/Main Page)',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 2500'
            },
            {
              id: 'avgWordsPerSentence',
              label: 'Average Words Per Sentence',
              type: 'decimal',
              required: true,
              min: 0,
              placeholder: 'e.g. 7.5'
            },
            {
              id: 'fleschScore',
              label: 'Flesch Reading Ease Score',
              type: 'decimal',
              required: true,
              min: 0,
              max: 100,
              placeholder: 'e.g. 68.5'
            }
          ],
          recommendationTemplate: 'Maintain readable copy around 60-63 Flesch Reading Ease with concise sentence structure.'
        },
        {
          id: 'step-static-vs-dynamic',
          sectionId: 'sec-content-keyword-focus',
          stepNumber: 17,
          title: 'Determine Static vs. Dynamic Content Growth',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Website Review',
          what: 'Evaluate whether the site regularly adds fresh blog content (dynamic) or remains static (content uploaded once at build; no recent updates).',
          why: 'Google favors sites that update content regularly over static sites that receive no updates after initial build.',
          how: [
            'HOW TO OBSERVE AS A BEGINNER:',
            '1. Open the client website homepage and look for "Blog", "News", "Articles", or "Resources" in the header or footer menu.',
            '2. Open the 3 most recent articles or posts.',
            '3. Check the publication date shown under the article title (e.g., "Published July 14, 2026" vs "Published 2 years ago").',
            '4. EVALUATE CONTENT GROWTH STATUS:',
            '   • Active Dynamic Site: New posts added regularly (weekly/monthly).',
            '   • Stalled Static Site: Posts are dated several months/years ago with NO new posts since.',
            '   • Fixed Static Site: No blog or news section exists at all.',
            '5. Select the matching option below.'
          ],
          fields: [
            {
              id: 'contentGrowthClassification',
              label: 'Content Growth Observation Checklist *',
              type: 'select',
              required: true,
              options: [
                { label: 'Dynamic Site — Active Content Growth (New articles/blogs published regularly)', value: 'dynamic_regular' },
                { label: 'Static Site — Stalled Content Growth (Blog posts dated months/years ago; no recent updates added)', value: 'static_stalled' },
                { label: 'Static Site — Fixed Pages Only (No blog/news section present on website)', value: 'static_fixed_only' }
              ]
            },
            {
              id: 'additionalComments',
              label: 'Additional Auditor Observations / Comments (Optional)',
              type: 'text',
              required: false,
              placeholder: 'e.g. Last blog post was published 8 months ago. No new content has been added since.'
            }
          ]
        }
      ]
    },
    {
      id: 'sec-images',
      title: 'Images & Performance Optimization',
      icon: 'Image',
      description: 'Audit image sizes, alt text, alt attributes, and size attributes.',
      steps: [
        {
          id: 'step-images-size',
          sectionId: 'sec-images-multimedia',
          stepNumber: 18,
          title: 'Audit Image Sizes (>100 KB Threshold)',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Find images exceeding 100 KB in size.',
          why: 'Large images slow down page loading speed. Loading speed is a critical Google ranking factor directly influencing user bounce rates.',
          how: [
            'In Screaming Frog, select the "Images" tab.',
            'Check total images count.',
            'Select Filter: "Over 100 KB" and record the count.',
            'Paste sample image URLs exceeding 100 KB.'
          ],
          fields: [
            {
              id: 'totalImages',
              label: 'Total Images Found',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 133'
            },
            {
              id: 'imagesOver100k',
              label: 'Number of Images Over 100 KB',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalImages',
              placeholder: 'e.g. 126'
            },
            {
              id: 'imagesOver100kUrls',
              label: 'Sample Large Image URLs (>100 KB)',
              type: 'text',
              required: false,
              placeholder: 'Paste sample image URLs exceeding 100 KB (one per line)',
              helpText: 'Record URLs of oversized images to compress.'
            }
          ],
          calculations: [
            {
              id: 'imagesOver100kPct',
              label: 'Over 100 KB Images Percentage',
              formula: 'percentage',
              affectedFieldId: 'imagesOver100k',
              totalFieldId: 'totalImages',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Compress all images exceeding 100 KB to improve page load speed.'
        },
        {
          id: 'step-images-alt',
          sectionId: 'sec-images-multimedia',
          stepNumber: 19,
          title: 'Audit Missing Image Alt Text & Attributes',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Audit missing alternative text, missing alt attribute code tags, and missing size attributes.',
          why: 'Search engines cannot see images visually; alt text tells Google what the image depicts. Missing alt attribute means the alt HTML code tag is completely absent.',
          how: [
            'In Screaming Frog under "Images" tab, select Filter: "Missing Alt Text" and record count.',
            'Select Filter: "Missing Alt Attribute" and record count.',
            'Select Filter: "Missing Size Attributes" (width/height dimensions) and record count.',
            'Paste sample image URLs missing alt text and attributes.'
          ],
          dependencies: ['step-images-size'],
          fields: [
            {
              id: 'missingAltText',
              label: 'Images Missing Alt Text',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalImages',
              placeholder: 'e.g. 40'
            },
            {
              id: 'missingAltTextUrls',
              label: 'Sample Image URLs Missing Alt Text',
              type: 'text',
              required: false,
              placeholder: 'Paste sample image URLs missing alt text (one per line)',
              helpText: 'Record sample image URLs missing alt text for remediation.'
            },
            {
              id: 'missingAltAttribute',
              label: 'Images Missing Alt Attribute Code Tag',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalImages',
              placeholder: 'e.g. 0'
            },
            {
              id: 'missingAltAttributeUrls',
              label: 'Sample Image URLs Missing Alt Attribute Code Tag',
              type: 'text',
              required: false,
              placeholder: 'Paste sample image URLs missing alt attribute tag (one per line)',
              helpText: 'Record sample image URLs missing alt attribute tag.'
            },
            {
              id: 'missingSizeAttributes',
              label: 'Images Missing Size Attributes (Width/Height)',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalImages',
              placeholder: 'e.g. 194'
            },
            {
              id: 'missingSizeAttributesUrls',
              label: 'Sample Image URLs Missing Size Attributes',
              type: 'text',
              required: false,
              placeholder: 'Paste sample image URLs missing width/height attributes (one per line)',
              helpText: 'Record sample image URLs missing size attributes.'
            }
          ],
          recommendationTemplate: 'Add descriptive alt text containing keywords to all images missing alt text.'
        }
      ]
    },
    {
      id: 'sec-urls',
      title: 'Screaming Frog — URL Structure',
      icon: 'Link',
      description: 'Audit URL lengths, cleanliness, underscores, and parameters.',
      steps: [
        {
          id: 'step-url-structure',
          sectionId: 'sec-architecture-urls',
          stepNumber: 20,
          title: 'Audit URL Length & Formatting Rules',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Audit long URLs (>115 chars vs <100 chars recommendation), underscores, repetitive paths, spaces, parameters, and tracking codes.',
          why: 'Clean, concise URLs (<100 chars) using hyphens are easy for users to read and remember, avoiding technical ranking penalties.',
          how: [
            'In Screaming Frog, select the "URL" tab.',
            'Filter by "Over 115 Characters" (record count).',
            'Check for URLs containing underscores (_), repetitive paths (/page1/page1), spaces, or parameters.',
            'Paste sample long or unclean URLs for client review.'
          ],
          fields: [
            {
              id: 'totalUrls',
              label: 'Total URLs Evaluated',
              type: 'integer',
              required: true,
              min: 1,
              placeholder: 'e.g. 68'
            },
            {
              id: 'urlsOver115',
              label: 'URLs Over 115 Characters',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalUrls',
              placeholder: 'e.g. 54'
            },
            {
              id: 'urlsOver115List',
              label: 'Sample URLs Over 115 Characters',
              type: 'text',
              required: false,
              placeholder: 'Paste long URLs over 115 characters (one per line)',
              helpText: 'List sample long URLs to shorten in the client report.'
            },
            {
              id: 'urlsWithUnderscores',
              label: 'URLs Containing Underscores',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalUrls',
              placeholder: 'e.g. 0'
            },
            {
              id: 'urlsWithUnderscoresList',
              label: 'Sample URLs Containing Underscores',
              type: 'text',
              required: false,
              placeholder: 'Paste URLs containing underscores (one per line)',
              helpText: 'List sample URLs containing underscores.'
            },
            {
              id: 'urlsRepetitive',
              label: 'URLs with Repetitive Paths',
              type: 'integer',
              required: true,
              min: 0,
              maxRefFieldId: 'totalUrls',
              placeholder: 'e.g. 0'
            },
            {
              id: 'urlsRepetitiveList',
              label: 'Sample URLs with Repetitive Paths',
              type: 'text',
              required: false,
              placeholder: 'Paste URLs with repetitive paths (one per line)',
              helpText: 'List sample URLs with repetitive directory paths.'
            }
          ],
          calculations: [
            {
              id: 'urlsOver115Pct',
              label: 'Long URLs Percentage',
              formula: 'percentage',
              affectedFieldId: 'urlsOver115',
              totalFieldId: 'totalUrls',
              unit: '%'
            }
          ],
          recommendationTemplate: 'Keep URLs under 100 characters, use hyphens instead of underscores, and eliminate repetitive directory paths.'
        }
      ]
    },
    {
      id: 'sec-links-status',
      title: 'Links & HTTP Response Codes',
      icon: 'Activity',
      description: 'Audit internal broken links (404/403), external links, and 3xx redirects.',
      steps: [
        {
          id: 'step-internal-links',
          sectionId: 'sec-architecture-urls',
          stepNumber: 21,
          title: 'Audit Internal Links & Status Codes',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Find internal broken links (404 Not Found, 403 Forbidden) and check status code distributions.',
          why: 'Broken links (404/403) kill user experience and waste search engine crawl budget. Healthy links return 200 OK.',
          how: [
            'In Screaming Frog, select the "Internal" tab and set Filter to "HTML".',
            'Look at the "Status Code" column.',
            'Record counts for 200 OK, 3xx Redirects, 403 Forbidden, 404 Broken links, and 5xx Server Errors.',
            'Paste URLs of broken internal links (404/403) and 3xx redirects to include in report.'
          ],
          fields: [
            {
              id: 'count200Ok',
              label: '200 OK Healthy Links Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 1300'
            },
            {
              id: 'count3xxRedirects',
              label: '3xx Redirected Links (301, 302, 308)',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 29'
            },
            {
              id: 'count3xxRedirectsUrls',
              label: 'Sample 3xx Redirected Page URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste 3xx redirected URLs (one per line)',
              helpText: 'Record sample 3xx redirected URLs.'
            },
            {
              id: 'count403Forbidden',
              label: '403 Forbidden Pages Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            },
            {
              id: 'count403ForbiddenUrls',
              label: 'Sample 403 Forbidden Page URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste 403 forbidden URLs (one per line)',
              helpText: 'Record sample 403 forbidden URLs.'
            },
            {
              id: 'count404Broken',
              label: '404 Broken Internal Links Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            },
            {
              id: 'brokenInternalUrlsList',
              label: 'Broken Internal Page URLs (404 / 403)',
              type: 'text',
              required: false,
              placeholder: 'Paste broken internal URLs (one per line)',
              helpText: 'Record specific broken internal link URLs.'
            },
            {
              id: 'count5xxServer',
              label: '5xx Server Errors (Host Issue)',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            }
          ],
          recommendationTemplate: 'Fix all 404 broken internal links and 403 forbidden pages to ensure seamless navigation.'
        },
        {
          id: 'step-external-links',
          sectionId: 'sec-architecture-urls',
          stepNumber: 22,
          title: 'Audit Outbound External Links',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Audit external links pointing from client website to external reference sites.',
          why: 'Linking out to authoritative niche sources (Wikipedia, government sites) establishes relevance, but broken outbound links harm credibility.',
          how: [
            'In Screaming Frog, select the "External" tab and set Filter to "HTML".',
            'Check status codes of outbound links (verify no 404/403 broken outbound links).',
            'Paste any broken external URLs.'
          ],
          fields: [
            {
              id: 'totalExternalLinks',
              label: 'Total Outbound External Links',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 45'
            },
            {
              id: 'brokenExternalLinks',
              label: 'Broken External Links (404/403)',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            },
            {
              id: 'brokenExternalUrlsList',
              label: 'Broken External Outbound URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste broken external outbound URLs (one per line)',
              helpText: 'Record broken external URLs.'
            }
          ],
          recommendationTemplate: 'Update or remove broken external outbound links to maintain reference credibility.'
        }
      ]
    },
    {
      id: 'sec-security-ssl',
      title: 'Security & SSL (HTTPS)',
      icon: 'ShieldCheck',
      description: 'Audit SSL security certificates and mixed content vulnerabilities.',
      steps: [
        {
          id: 'step-ssl-security',
          sectionId: 'sec-technical-security',
          stepNumber: 23,
          title: 'Audit SSL Certificate & HTTPS Protocol',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Browser Address Bar / Screaming Frog',
          what: 'Verify if SSL certificate is installed and active on the website (HTTPS vs HTTP).',
          why: 'SSL provides encrypted connections. Google treats HTTPS as a core security requirement and ranking signal.',
          how: [
            'Open the client website in Google Chrome.',
            'Check the address bar lock icon ("Connection is secure").',
            'In Screaming Frog under Security tab, record HTTP (unsecure) vs HTTPS (secure) URL counts.'
          ],
          fields: [
            {
              id: 'httpCount',
              label: 'Unsecure HTTP URLs Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            },
            {
              id: 'httpsCount',
              label: 'Secure HTTPS URLs Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 277'
            },
            {
              id: 'sslActive',
              label: 'Is SSL Certificate Active & Secure?',
              type: 'select',
              required: true,
              options: [
                { label: 'Yes — HTTPS Active & Secure', value: 'yes' },
                { label: 'No — HTTP Unsecure', value: 'no' }
              ]
            }
          ],
          recommendationTemplate: 'Install and enforce SSL across all domain URLs to secure connection and maintain search rankings.'
        },
        {
          id: 'step-mixed-content',
          sectionId: 'sec-technical-security',
          stepNumber: 24,
          title: 'Audit Mixed Content Issues',
          lecture: 32,
          coverage: 'explicit',
          tool: 'Screaming Frog',
          what: 'Check for unsecure HTTP elements (images/scripts) loading on secure HTTPS pages.',
          why: 'Mixed content weakens security and causes browser security warnings, reducing rankings.',
          how: [
            'In Screaming Frog, select the "Security" tab.',
            'Select Filter: "Mixed Content".',
            'Record the count of mixed content URLs and paste sample mixed content URLs.'
          ],
          fields: [
            {
              id: 'mixedContentCount',
              label: 'Mixed Content URLs Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 0'
            },
            {
              id: 'mixedContentUrlsList',
              label: 'Sample Mixed Content Asset URLs',
              type: 'text',
              required: false,
              placeholder: 'Paste sample HTTP asset URLs loading on HTTPS pages (one per line)',
              helpText: 'Record sample mixed content URLs.'
            }
          ],
          recommendationTemplate: 'Resolve mixed content issues (e.g. using Really Simple SSL plugin) so all page assets load via HTTPS.'
        }
      ]
    },
    {
      id: 'sec-technical-tools',
      title: 'Technical Indexation & External Tools',
      icon: 'Globe',
      description: 'Audit Google indexation, robots.txt, sitemap, Siteliner, W3C, and Pingdom speed.',
      steps: [
        {
          id: 'step-indexation-google',
          sectionId: 'sec-technical-security',
          stepNumber: 25,
          title: 'Audit Google Indexation (site: search & SEOquake)',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Google Search & SEOquake',
          toolUrl: 'https://chrome.google.com/webstore/detail/seoquake/akdgnmcogleamklkdmflpdjbdbilbmap',
          what: 'Compare Google indexed page count (site:domain.com search + SEOquake Chrome extension) against Screaming Frog crawl count.',
          why: 'Distinguish between actual web Pages vs URLs (URLs include attachments, parameter variations, images). Verify if Google has indexed all main pages.',
          how: [
            'HOW TO USE SEOQUAKE & GOOGLE SITE SEARCH (BEGINNER STEP-BY-STEP):',
            '1. Install free "SEOquake" Chrome Extension from Chrome Web Store.',
            '2. Open Google Search and type: site:yourclientdomain.com (e.g. site:nkfi.org).',
            '3. Read Google Index Count: Look at the top of Google search results for "About X results" (e.g. 269 results). Record this number below.',
            '4. Read SEOquake Extension Count: Click the SEOquake icon in your Chrome top bar while visiting the website. Look at the row labeled "Google Index" or "Google". Record the number shown (e.g. 200).',
            '5. Compare with Screaming Frog crawl count to check if pages are missing in Google or over-indexed due to parameters/attachments.',
            '6. Select the indexation observation checklist option below and upload a screenshot of the SEOquake / Google site search result.'
          ],
          requiresEvidence: true,
          evidencePrompt: 'Upload a screenshot of SEOquake popup or Google search results for site:yourdomain.com',
          fields: [
            {
              id: 'googleIndexCount',
              label: 'Google Indexed Results Count (site: search)',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 269'
            },
            {
              id: 'seoquakeCount',
              label: 'SEOquake Extension Google Index Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 200'
            },
            {
              id: 'indexationClassification',
              label: 'Indexation Observation Checklist *',
              type: 'select',
              required: true,
              options: [
                { label: 'Normal Indexation — Google index count aligns with Screaming Frog crawled pages', value: 'normal_indexation' },
                { label: 'Under-Indexed — Google index count is lower than crawled pages (missing pages in SERP)', value: 'under_indexed' },
                { label: 'Over-Indexed — Google index count is higher due to parameters, attachment pages, or image URLs', value: 'over_indexed_params' }
              ]
            },
            {
              id: 'additionalComments',
              label: 'Additional Auditor Observations / Comments (Optional)',
              type: 'text',
              required: false,
              placeholder: 'e.g. Google site search shows 269 URLs indexed while tool crawled 150 static pages.'
            }
          ],
          recommendationTemplate: 'Submit missing pages via Google Search Console to ensure full indexation.'
        },
        {
          id: 'step-robotstxt',
          sectionId: 'sec-technical-security',
          stepNumber: 26,
          title: 'Verify robots.txt File',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Browser Direct URL',
          what: 'Check if robots.txt file exists and provides proper instructions to crawlers.',
          why: 'robots.txt gives search engines instructions on which areas of the website to crawl or exclude.',
          how: [
            'Open browser and navigate to: clientdomain.com/robots.txt',
            'Verify if file loads successfully and contains valid crawler directives.'
          ],
          fields: [
            {
              id: 'hasRobotsTxt',
              label: 'Does robots.txt exist and function properly?',
              type: 'select',
              required: true,
              options: [
                { label: 'Yes — Valid robots.txt Present', value: 'yes' },
                { label: 'No — Missing or Broken robots.txt', value: 'no' }
              ]
            }
          ],
          recommendationTemplate: 'Ensure a valid robots.txt file exists in the domain root directory.'
        },
        {
          id: 'step-sitemap',
          sectionId: 'sec-technical-security',
          stepNumber: 27,
          title: 'Verify XML Sitemap Blueprint',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Browser Direct URL',
          what: 'Verify existence of XML sitemap file.',
          why: 'The XML sitemap acts as the website blueprint, allowing Google crawlers to easily discover all pages.',
          how: [
            'Navigate to clientdomain.com/sitemap.xml (or check robots.txt reference).',
            'Verify if sitemap is present and properly formatted.'
          ],
          fields: [
            {
              id: 'hasXmlSitemap',
              label: 'Does XML Sitemap exist?',
              type: 'select',
              required: true,
              options: [
                { label: 'Yes — XML Sitemap Present', value: 'yes' },
                { label: 'No — XML Sitemap Missing', value: 'no' }
              ]
            }
          ],
          recommendationTemplate: 'Generate and submit an XML sitemap to search engines.'
        },
        {
          id: 'step-siteliner',
          sectionId: 'sec-performance-speed',
          stepNumber: 28,
          title: 'Check Siteliner Duplicate Content %',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Siteliner',
          toolUrl: 'https://www.siteliner.com/',
          what: 'Run domain on Siteliner to measure duplicate content percentage.',
          why: 'Siteliner identifies internal duplicate content across site pages. Average web benchmark is ~8% duplicate content.',
          how: [
            '1. Open Siteliner.com in browser.',
            '2. Enter client website URL and click "Go".',
            '3. SITELINER ACCESS RESTRICTIONS / BYPASS OPTION:',
            '   • If Siteliner shows "unable to reach the home page on your site" (firewall/security block) or site requires login:',
            '   • Select "Bypass — Siteliner blocked by client firewall / server security" below.',
            '   • Upload a screenshot of the Siteliner error page as evidence.',
            '4. IF ACCESSIBLE: Record Duplicate Content %, Common Content %, and Unique Content %.'
          ],
          requiresEvidence: true,
          evidencePrompt: 'Upload a screenshot of the Siteliner report summary or error page',
          fields: [
            {
              id: 'sitelinerAccessStatus',
              label: 'Siteliner Access & Scan Status *',
              type: 'select',
              required: true,
              options: [
                { label: 'Accessible — Siteliner scanned website successfully', value: 'accessible' },
                { label: 'Bypass — Siteliner blocked by client firewall/server security (Unable to reach home page)', value: 'blocked_bypass' },
                { label: 'Bypass — Website requires login / no client server access to whitelist IP yet', value: 'unreachable_bypass' }
              ],
              defaultValue: 'accessible'
            },
            {
              id: 'duplicateContentPct',
              label: 'Duplicate Content Percentage (%)',
              type: 'decimal',
              required: false,
              min: 0,
              max: 100,
              placeholder: 'e.g. 22 (Leave empty if bypassed)'
            },
            {
              id: 'commonContentPct',
              label: 'Common Content Percentage (%)',
              type: 'decimal',
              required: false,
              min: 0,
              max: 100,
              placeholder: 'e.g. 42 (Leave empty if bypassed)'
            },
            {
              id: 'uniqueContentPct',
              label: 'Unique Content Percentage (%)',
              type: 'decimal',
              required: false,
              min: 0,
              max: 100,
              placeholder: 'e.g. 52 (Leave empty if bypassed)'
            },
            {
              id: 'additionalComments',
              label: 'Additional Auditor Notes / Bypass Rationale',
              type: 'text',
              required: false,
              placeholder: 'e.g. Siteliner was unable to reach home page due to client server User-Agent firewall rules.'
            }
          ],
          recommendationTemplate: 'Whitelist Siteliner crawler User-Agent / IP range (46.4.250.49-46.4.250.52) on client firewall to analyze duplicate content.'
        },
        {
          id: 'step-w3c-validation',
          sectionId: 'sec-performance-speed',
          stepNumber: 29,
          title: 'W3C Code Validation Check',
          lecture: 33,
          coverage: 'explicit',
          tool: 'W3C Markup Validation Service',
          toolUrl: 'https://validator.w3.org/',
          what: 'Validate site code for HTML syntax errors using W3C Validator.',
          why: 'Severe HTML code errors impede crawler parsing and browser rendering.',
          how: [
            '1. Open validator.w3.org.',
            '2. Enter client website URL and click "Check".',
            '3. Click "Message Filter" to isolate error count.',
            '4. Record error count and upload a screenshot of W3C validation output.'
          ],
          requiresEvidence: true,
          evidencePrompt: 'Upload screenshot of W3C Markup Validation result',
          fields: [
            {
              id: 'w3cErrorCount',
              label: 'Number of W3C Code Errors Found',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 15'
            }
          ],
          recommendationTemplate: 'Fix W3C code validation errors to ensure clean HTML structure.'
        },
        {
          id: 'step-pingdom-speed',
          sectionId: 'sec-performance-speed',
          stepNumber: 30,
          title: 'Pingdom Page Loading Speed Test',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Pingdom Tools',
          toolUrl: 'https://tools.pingdom.com/',
          what: 'Test site load time, performance grade, and page size using Pingdom.',
          why: 'Page speed is a major ranking factor. Fast sites reduce user bounce rates.',
          how: [
            '1. Open tools.pingdom.com.',
            '2. Enter client URL, select testing location, and click "Start Test".',
            '3. Record Load Time (seconds), Performance Grade, and Total Page Size (MB).',
            '4. Upload a screenshot of the Pingdom test result & resource summary chart.'
          ],
          requiresEvidence: true,
          evidencePrompt: 'Upload screenshot of Pingdom Speed Test result',
          fields: [
            {
              id: 'pingdomLoadTime',
              label: 'Load Time (seconds)',
              type: 'decimal',
              required: true,
              min: 0,
              placeholder: 'e.g. 2.45'
            },
            {
              id: 'pingdomGrade',
              label: 'Performance Grade / Score',
              type: 'string',
              required: true,
              placeholder: 'e.g. B 82'
            },
            {
              id: 'pingdomPageSize',
              label: 'Total Page Size',
              type: 'string',
              required: true,
              placeholder: 'e.g. 3.2 MB'
            }
          ],
          recommendationTemplate: 'Optimize site assets and images to reduce page load time under 2 seconds.'
        }
      ]
    },
    {
      id: 'sec-offpage-social',
      title: 'Off-Page SEO & Social Media',
      icon: 'Share2',
      description: 'Audit Moz Domain Authority, backlink metrics, and social media presence.',
      steps: [
        {
          id: 'step-moz-authority',
          sectionId: 'sec-offpage-social',
          stepNumber: 31,
          title: 'Moz Link Explorer — Domain Authority & Links',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Moz Link Explorer',
          toolUrl: 'https://moz.com/link-explorer',
          what: 'Record Domain Authority (DA), Linking/Referring Domains, and total Inbound Links using Moz.',
          why: 'Off-page SEO represents ~34% of search engine ranking algorithm weight. External domain authority and quality backlinks build search engine trust.',
          how: [
            '1. Open Moz Link Explorer (moz.com/link-explorer) and sign up / log in.',
            '2. Enter client domain URL in the search box.',
            '3. Record Domain Authority (0–100 score), Linking Domains count, and Inbound Links count.',
            '4. Upload a screenshot of the Moz summary overview as evidence.'
          ],
          requiresEvidence: true,
          evidencePrompt: 'Upload screenshot of Moz Link Explorer summary dashboard',
          fields: [
            {
              id: 'domainAuthority',
              label: 'Domain Authority (DA 0–100)',
              type: 'integer',
              required: true,
              min: 0,
              max: 100,
              placeholder: 'e.g. 30'
            },
            {
              id: 'linkingDomains',
              label: 'Linking / Referring Domains Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 705'
            },
            {
              id: 'inboundLinks',
              label: 'Total Inbound Links Count',
              type: 'integer',
              required: true,
              min: 0,
              placeholder: 'e.g. 12500'
            }
          ],
          recommendationTemplate: 'Acquire high-authority referring domain links to strengthen Domain Authority.'
        },
        {
          id: 'step-social-media',
          sectionId: 'sec-offpage-social',
          stepNumber: 32,
          title: 'Audit Social Media Accounts & Presence',
          lecture: 33,
          coverage: 'explicit',
          tool: 'Client Website Footer/Header',
          what: 'Check if brand social media accounts (Facebook, Twitter/X, Instagram, LinkedIn, YouTube, Pinterest, TikTok) are active and linked from the website.',
          why: 'Social media profiles enhance brand visibility and provide additional authority signals.',
          how: [
            '1. Open client website homepage.',
            '2. Locate social media icons in header or footer.',
            '3. Click each profile link to verify active accounts.',
            '4. Select all active social media channels found below (selecting even 1 channel is valid).'
          ],
          fields: [
            {
              id: 'socialChannelsList',
              label: 'Active Social Media Channels Found *',
              type: 'multiselect',
              required: true,
              options: [
                { label: 'Facebook', value: 'Facebook' },
                { label: 'Twitter / X', value: 'Twitter/X' },
                { label: 'Instagram', value: 'Instagram' },
                { label: 'LinkedIn', value: 'LinkedIn' },
                { label: 'YouTube', value: 'YouTube' },
                { label: 'Pinterest', value: 'Pinterest' },
                { label: 'TikTok', value: 'TikTok' },
                { label: 'None (No active accounts found)', value: 'None' }
              ],
              helpText: 'Click to select active channels found. Selecting 1 channel is valid and sufficient.'
            },
            {
              id: 'socialMediaClassification',
              label: 'Social Media Assessment Checklist *',
              type: 'select',
              required: true,
              options: [
                { label: 'Active Presence — Official brand social accounts active and linked in header/footer', value: 'active_linked' },
                { label: 'Broken Presence — Social icons exist on website, but links are broken or point to template defaults', value: 'unlinked_broken' },
                { label: 'No Presence — No social media icons or accounts found on website', value: 'no_social_presence' }
              ]
            },
            {
              id: 'additionalComments',
              label: 'Additional Auditor Observations / Comments (Optional)',
              type: 'text',
              required: false,
              placeholder: 'e.g. Facebook profile is active, but Twitter link in footer points to template default.'
            }
          ],
          recommendationTemplate: 'Link and maintain active social media accounts in the website footer/header.'
        }
      ]
    }
  ]
};
